import dayjs from 'dayjs';
import TrainingClassMeta from 'pollworker/src/components/training/TrainingClassMeta';
import { useAppSelector } from 'pollworker/src/hooks';
import { TrainingClassStatus, getTrainingStatusMessage } from 'pollworker/src/state/training';
import type { UserTrainingClass } from 'pollworker/src/state/training';
import { apiDateConverter } from 'pollworker/src/utils/apiDateConverter';
import { useState } from 'react';
import { Flexor } from 'shared/src/components';
import { Badge, Button, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle, buttonVariants } from 'shared/src/components/ui/new';
import { cn, telHref } from 'shared/src/utils';

interface TrainingClassRowProps {
  trainingClass: UserTrainingClass;
  onAction?: () => void;
  actionLabel?: string;
  actionVariant?: 'default' | 'outline';
  disabled?: boolean;
  completed?: boolean;
  cancelled?: boolean;
  hasOnlineOption?: boolean;
}

export default function TrainingClassRow({
  trainingClass,
  onAction,
  actionLabel,
  actionVariant = 'default',
  disabled,
  completed,
  cancelled,
  hasOnlineOption,
}: TrainingClassRowProps) {
  const currentUser = useAppSelector((state) => state.currentPollworkerUser);
  const [showCallOfficeDialog, setShowCallOfficeDialog] = useState(false);
  const labels: string[] = [];

  if (trainingClass.IsExtraTraining) labels.push('Additional training');
  if (trainingClass.AttendanceNotRecorded) labels.push('Attendance not recorded');
  if (trainingClass.IsVoided && completed) labels.push('No longer required');

  const bookedSessionLine = getBookedSessionLine(trainingClass);
  const statusMessage =
    trainingClass.Status === TrainingClassStatus.Scheduled && bookedSessionLine
      ? bookedSessionLine
      : trainingClass.Status === TrainingClassStatus.NeedsAction
        ? null
        : getTrainingStatusMessage(trainingClass);
  const unavailable = trainingClass.Status === TrainingClassStatus.Unavailable;
  const callHref = telHref(currentUser.Customer.Phone);

  return (
    <>
      <Dialog open={showCallOfficeDialog} onOpenChange={setShowCallOfficeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Call the office</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-gray-700">Please call your election office for help scheduling this training. The phone number is not available here.</p>
          </DialogBody>
          <DialogFooter>
            <Button size="sm" onClick={() => setShowCallOfficeDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div
        className={cn(
          'flex flex-col items-center relative justify-between gap-x-4 gap-y-4 rounded-md bg-white p-4 shadow-sm sm:flex-row',
          disabled || cancelled ? 'opacity-60' : '',
        )}
      >
        {import.meta.env.DEV && trainingClass.RequirementId?.length ? (
          <p className="absolute top-3 right-3 mt-1 font-mono text-xs text-gray-600">Req: {trainingClass.RequirementId.map((rid) => rid.substring(0, 7)).join(', ')}</p>
        ) : null}
        <div className="min-w-0 flex-1 w-full">
          <div className="flex flex-wrap items-center gap-2">
            <Flexor className="space-x-3">
              <p className={cn('text-base font-semibold', completed ? 'text-green-700' : cancelled ? 'text-gray-600' : 'text-gray-900')}>{trainingClass.ClassName}</p>
            </Flexor>
            {import.meta.env.DEV && trainingClass.IsLegacy ? (
              <Badge status="warning" className="px-2 py-0.5">
                LEGACY
              </Badge>
            ) : null}
            {labels.map((label) => (
              <Badge key={label} status="blank" className="px-2 py-0.5 font-medium">
                {label}
              </Badge>
            ))}
            {hasOnlineOption ? (
              <Badge status="info" className="px-2 py-0.5 font-medium">
                Online course
              </Badge>
            ) : null}
          </div>
          <TrainingClassMeta trainingClass={trainingClass} statusMessage={statusMessage} completed={completed} cancelled={cancelled} />
        </div>

        {unavailable && !onAction ? (
          <div className="flex w-full flex-shrink-0 flex-col gap-2 sm:w-auto sm:flex-row">
            {callHref ? (
              <a href={callHref} className={cn(buttonVariants({ size: 'sm' }), 'w-full')}>
                Call the office
              </a>
            ) : (
              <Button size="sm" className="w-full" onClick={() => setShowCallOfficeDialog(true)}>
                Call the office
              </Button>
            )}
          </div>
        ) : null}
        {onAction && actionLabel ? (
          <Button size="sm" variant={actionVariant} onClick={onAction} disabled={disabled} className="sm:w-auto w-full">
            {actionLabel}
          </Button>
        ) : null}
      </div>
    </>
  );
}

function getBookedSessionLine(trainingClass: UserTrainingClass) {
  const session = trainingClass.SelectedSession;
  if (session?.FirstStart) {
    const when = dayjs(session.FirstStart).format('MMM D [at] h:mm A');
    return session.Location ? `${when} — ${session.Location}` : `Scheduled for ${when}`;
  }

  if (!trainingClass.SessionDate) return null;

  const when = dayjs(apiDateConverter(trainingClass.SessionDate)).format('MMM D [at] h:mm A');
  return trainingClass.SessionLocation ? `${when} — ${trainingClass.SessionLocation}` : `Scheduled for ${when}`;
}
